// frontend/src/pages/Dashboard.jsx
import React, { useState, useEffect } from 'react';
import {
  Container, Grid, Paper, Typography, Box, Card, CardContent,
  CardActions, Button, IconButton, Menu, MenuItem, Chip, CircularProgress
} from '@mui/material';
import {
  People as PeopleIcon,
  Assignment as LeadIcon,
  Description as QuotationIcon,
  TrendingUp as TrendingIcon,
  Business as DepartmentIcon,
  BarChart as ReportIcon,
  Settings as SettingsIcon,
  AccountCircle,
  Add as AddIcon,
  Notifications as NotificationsIcon,
  Task as TaskIcon,
  Phone as PhoneIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { canAccessPage } from '../utils/pagePermissions';
import ChangePasswordModal from '../components/ChangePasswordModal';
import MyProfileModal from '../components/MyProfileModal';
import leadAPI from '../api/leadAPI';
import quotationAPI from '../api/quotationAPI';
import taskAPI from '../api/taskAPI';
import callLogAPI from '../api/callLogAPI';

const ModernStatCard = ({ title, value, icon, gradient }) => (
  <Paper sx={{
    p: { xs: 2, sm: 3 },
    borderRadius: 3,
    background: gradient,
    color: 'white',
    position: 'relative',
    overflow: 'hidden',
    minHeight: { xs: 120, sm: 160 },
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    '&::before': {
      content: '""', position: 'absolute', top: -30, right: -30,
      width: 80, height: 80, background: 'rgba(255,255,255,0.1)', borderRadius: '50%'
    }
  }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
      <Box sx={{ zIndex: 1, position: 'relative' }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 0.5, opacity: 0.9, fontSize: { xs: '0.85rem', sm: '1.1rem' } }}>
          {title}
        </Typography>
        <Typography sx={{ fontWeight: 'bold', fontSize: { xs: '1.8rem', sm: '2.2rem' }, lineHeight: 1 }}>
          {value}
        </Typography>
      </Box>
      <Box sx={{ opacity: 0.8, zIndex: 1, position: 'relative' }}>
        {icon}
      </Box>
    </Box>
  </Paper>
);

const FeatureCard = ({ title, description, icon, onClick, color }) => (
  <Card sx={{
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    borderRadius: 3,
    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
    transition: 'all 0.3s ease',
    border: '1px solid',
    borderColor: 'divider',
    '&:hover': {
      transform: 'translateY(-6px)',
      boxShadow: '0 12px 40px rgba(0,0,0,0.15)',
      borderColor: color
    }
  }}>
    <CardContent sx={{ flexGrow: 1, textAlign: 'center', py: { xs: 2.5, sm: 3 }, px: { xs: 1.5, sm: 2 } }}>
      <Box sx={{
        color: color, mb: 1.5,
        '& .MuiSvgIcon-root': { fontSize: { xs: 32, sm: 40 } }
      }}>
        {icon}
      </Box>
      <Typography gutterBottom variant="h6" component="h2"
        sx={{ fontWeight: 'bold', mb: 0.75, fontSize: { xs: '0.9rem', sm: '1rem' } }}>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary"
        sx={{ lineHeight: 1.5, opacity: 0.8, fontSize: { xs: '0.72rem', sm: '0.8rem' }, display: { xs: 'none', sm: 'block' } }}>
        {description}
      </Typography>
    </CardContent>
    <CardActions sx={{ justifyContent: 'center', pb: 2, px: 2 }}>
      <Button
        size="small"
        variant="outlined"
        onClick={onClick}
        sx={{
          borderColor: color, color: color, borderRadius: 2,
          px: { xs: 2, sm: 3 }, fontWeight: 'bold', fontSize: { xs: '0.72rem', sm: '0.8rem' },
          '&:hover': { backgroundColor: color, color: 'white' }
        }}
      >
        Open
      </Button>
    </CardActions>
  </Card>
);

export default function Dashboard({ user, onLogout }) {
  const [stats, setStats] = useState({
    totalLeads: 0, totalUsers: 0, totalQuotations: 0,
    wonLeads: 0, lostLeads: 0, convertedQuotations: 0,
    pendingApprovals: 0
  });
  const [tasksStats, setTasksStats] = useState({ total: 0, pending: 0, overdue: 0 });
  const [todayTasks, setTodayTasks] = useState([]);
  const [tasksLoading, setTasksLoading] = useState(true);
  const [callStats, setCallStats] = useState({ total: 0, today: 0, interested: 0, callback: 0 });
  const [todayCalls, setTodayCalls] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [myProfileOpen, setMyProfileOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => { fetchStats(); fetchTaskData(); fetchCallData(); }, [user]);

  const fetchCallData = async () => {
    try {
      const [stats, calls] = await Promise.all([
        callLogAPI.getStats().catch(() => ({})),
        callLogAPI.getToday(5).catch(() => [])
      ]);
      setCallStats(stats || {});
      setTodayCalls(Array.isArray(calls) ? calls : []);
    } catch (e) { /* non-fatal */ }
  };

  const fetchTaskData = async () => {
    setTasksLoading(true);
    try {
      const [allTasks, stats] = await Promise.all([
        taskAPI.getTasks({ limit: 100 }).catch(() => []),
        taskAPI.getTaskStats ? taskAPI.getTaskStats().catch(() => null) : Promise.resolve(null)
      ]);

      const tasks = Array.isArray(allTasks) ? allTasks : [];
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const tomorrow = new Date(today); tomorrow.setDate(tomorrow.getDate() + 1);

      const todayList = tasks.filter(t => {
        if (!t.due_date) return false;
        const d = new Date(t.due_date);
        return d >= today && d < tomorrow;
      });

      setTodayTasks(todayList);

      if (stats) {
        setTasksStats({
          total: stats.total || tasks.length,
          pending: stats.pending || tasks.filter(t => t.status !== 'completed').length,
          overdue: stats.overdue || tasks.filter(t => {
            if (!t.due_date || t.status === 'completed') return false;
            return new Date(t.due_date) < today;
          }).length
        });
      } else {
        setTasksStats({
          total: tasks.length,
          pending: tasks.filter(t => t.status !== 'completed').length,
          overdue: tasks.filter(t => {
            if (!t.due_date || t.status === 'completed') return false;
            return new Date(t.due_date) < today;
          }).length
        });
      }
    } catch (e) {
      console.error('Task fetch error:', e);
    } finally {
      setTasksLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const isAdmin = user?.role === 'admin';
      const [leadStatsRes, leadPageRes, qStatsRes, qTotalsRes, usersRes] = await Promise.all([
        leadAPI.getLeadStats(),
        leadAPI.getLeadsPaginated({ page: 1, limit: 1 }),
        quotationAPI.getQuotationStats(),
        quotationAPI.getQuotationTotals(),
        isAdmin ? api.get('/users').then(r => r.data).catch(() => []) : Promise.resolve([])
      ]);

      const leadStats = Array.isArray(leadStatsRes) ? leadStatsRes : [];
      const totalLeads = Number(leadPageRes?.total || 0);
      const qStats = Array.isArray(qStatsRes) ? qStatsRes : [];
      const totalQuots = Number(qTotalsRes?.count || 0);

      const findStatusCount = (arr, ...names) => {
        for (const n of names) {
          const row = arr.find(s => String(s.status).toLowerCase() === String(n).toLowerCase());
          if (row) return Number(row.count) || 0;
        }
        return 0;
      };

      // Won and conversion come from the backend, not from lead statuses.
      // A lead is flipped to 'won' the moment a quotation is raised from it and
      // is never written back if that quotation is later lost, so counting
      // leads.status='won' reported quoted deals as wins. The real outcome is
      // quotations.deal_status, and the endpoint below is the single place that
      // arithmetic lives — the mobile app reads the same numbers.
      let dash = null;
      try { dash = await leadAPI.getDashboardStats(); } catch (_) { /* fall back below */ }

      setStats({
        totalLeads: dash ? dash.total_leads : totalLeads,
        totalUsers: (usersRes || []).length,
        totalQuotations: dash ? dash.quotations : totalQuots,
        wonLeads: dash ? dash.won : findStatusCount(leadStats, 'won'),
        lostLeads: findStatusCount(leadStats, 'lost'),
        pendingApprovals: dash ? dash.pending_approvals : findStatusCount(qStats, 'PendingApproval', 'pending'),
        convertedQuotations: findStatusCount(qStats, 'Approved'),
        conversionRate: dash ? dash.conversion_rate : null,
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const calculateConversionRate = () => {
    // Prefer the server's figure so this page, the mobile app and any report
    // quote the same number. The local calculation stays only as a fallback for
    // when the stats call fails.
    if (stats.conversionRate !== null && stats.conversionRate !== undefined) {
      return `${stats.conversionRate}%`;
    }
    const total = Number(stats.totalLeads) || 0;
    const won = Number(stats.wonLeads) || 0;
    if (total <= 0) return '0%';
    return ((won / total) * 100).toFixed(1) + '%';
  };

  const isManagerOrAdmin = ['manager', 'admin'].includes(user?.role?.toLowerCase());

  const systemModules = [
    canAccessPage(user?.role, 'users') && {
      title: 'Users', description: 'Manage system users, roles and permissions',
      icon: <PeopleIcon />, onClick: () => navigate('/users'), color: '#2e7d32'
    },
    canAccessPage(user?.role, 'departments') && {
      title: 'Departments', description: 'Organise your company structure',
      icon: <DepartmentIcon />, onClick: () => navigate('/departments'), color: '#1976d2'
    },
    canAccessPage(user?.role, 'leads') && {
      title: 'Leads', description: 'Track your sales pipeline',
      icon: <LeadIcon />, onClick: () => navigate('/leads'), color: '#ed6c02'
    },
    {
      title: 'Tasks', description: 'Daily tasks and follow-ups',
      icon: <TaskIcon />, onClick: () => navigate('/tasks'), color: '#ff9800'
    },
    {
      title: 'Call Logs', description: 'Track all customer calls and follow-ups',
      icon: <PhoneIcon />, onClick: () => navigate('/call-logs'), color: '#0288d1'
    },
    canAccessPage(user?.role, 'quotations') && {
      title: 'Quotations', description: 'Create and track quotations',
      icon: <QuotationIcon />, onClick: () => navigate('/quotations'), color: '#9c27b0'
    },
    canAccessPage(user?.role, 'reports') && {
      title: 'Reports', description: 'Analytics for business decisions',
      icon: <ReportIcon />, onClick: () => navigate('/reports'), color: '#d32f2f'
    },
    canAccessPage(user?.role, 'settings') && {
      title: 'Settings', description: 'Configure your CRM',
      icon: <SettingsIcon />, onClick: () => navigate('/settings'), color: '#7b1fa2'
    }
  ].filter(Boolean);

  return (
    <>
      <Container maxWidth="xl" sx={{ mt: 2, mb: 4 }}>
        {/* Header Banner */}
        <Paper sx={{
          p: { xs: 2.5, sm: 4 },
          mb: 4,
          background: 'linear-gradient(135deg, #1e3a8a 0%, #1d4ed8 50%, #0d9488 100%)',
          color: 'white', borderRadius: 3,
          boxShadow: '0 8px 32px rgba(30, 58, 138, 0.25)'
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 0.5, fontSize: { xs: '1.4rem', sm: '2rem' } }}>
                Welcome back, {user?.name}!
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.9, fontSize: { xs: '0.8rem', sm: '1rem' } }}>
                {new Date().toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
                <Chip label={`Role: ${user?.role}`} variant="outlined"
                  sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.3)', fontSize: '0.75rem' }} />
                {stats.pendingApprovals > 0 && isManagerOrAdmin && (
                  <Chip icon={<NotificationsIcon sx={{ fontSize: '1rem !important' }} />}
                    label={`${stats.pendingApprovals} Pending Approvals`}
                    color="warning" sx={{ fontSize: '0.75rem' }} />
                )}
                {todayTasks.length > 0 && (
                  <Chip icon={<TaskIcon sx={{ fontSize: '1rem !important' }} />}
                    label={`${todayTasks.length} Tasks Today`}
                    color="info" sx={{ fontSize: '0.75rem' }} />
                )}
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/leads')}
                sx={{ bgcolor: 'white', color: '#1e3a8a', fontWeight: 'bold', borderRadius: 2,
                  '&:hover': { bgcolor: '#f5f5f5' } }}>
                New Lead
              </Button>
              <IconButton sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.2)' }} onClick={e => setAnchorEl(e.currentTarget)}>
                <AccountCircle />
              </IconButton>
            </Box>
          </Box>
        </Paper>

        {/* KPI Cards */}
        <Grid container spacing={{ xs: 1.5, sm: 3 }} sx={{ mb: 4 }}>
          <Grid item xs={6} sm={6} md={3}>
            <ModernStatCard
              title={user?.role === 'admin' ? 'Total Leads' : 'My Leads'}
              value={stats.totalLeads}
              icon={<LeadIcon sx={{ fontSize: { xs: 28, sm: 40 } }} />}
              gradient="linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)"
            />
          </Grid>
          <Grid item xs={6} sm={6} md={3}>
            <ModernStatCard
              title={user?.role === 'admin' ? 'Total Quotations' : 'My Quotations'}
              value={stats.totalQuotations}
              icon={<QuotationIcon sx={{ fontSize: { xs: 28, sm: 40 } }} />}
              gradient="linear-gradient(135deg, #0d9488 0%, #115e59 100%)"
            />
          </Grid>
          <Grid item xs={6} sm={6} md={3}>
            <ModernStatCard
              title="Conversion Rate"
              value={calculateConversionRate()}
              icon={<TrendingIcon sx={{ fontSize: { xs: 28, sm: 40 } }} />}
              gradient="linear-gradient(135deg, #16a34a 0%, #14532d 100%)"
            />
          </Grid>
          {user?.role === 'admin' ? (
            <Grid item xs={6} sm={6} md={3}>
              <ModernStatCard
                title="Total Users"
                value={stats.totalUsers}
                icon={<PeopleIcon sx={{ fontSize: { xs: 28, sm: 40 } }} />}
                gradient="linear-gradient(135deg, #7c3aed 0%, #4c1d95 100%)"
              />
            </Grid>
          ) : (
            <Grid item xs={6} sm={6} md={3}>
              <ModernStatCard
                title="Won Deals"
                value={stats.wonLeads}
                icon={<TrendingIcon sx={{ fontSize: { xs: 28, sm: 40 } }} />}
                gradient="linear-gradient(135deg, #7c3aed 0%, #4c1d95 100%)"
              />
            </Grid>
          )}
        </Grid>

        {/* Performance + Tasks side by side */}
        <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: 4 }}>
          {/* Performance */}
          <Grid item xs={12} md={8}>
            <Paper sx={{
              p: { xs: 2.5, sm: 4 }, borderRadius: 3,
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
              border: '1px solid', borderColor: 'divider', height: '100%'
            }}>
              <Typography variant="h5" gutterBottom
                sx={{ fontWeight: 'bold', mb: 3, fontSize: { xs: '1rem', sm: '1.3rem' } }}>
                {user?.role === 'admin' ? 'Business Performance' : 'My Performance Overview'}
              </Typography>
              <Grid container spacing={{ xs: 1, sm: 3 }}>
                {[
                  { label: 'Won Deals', value: stats.wonLeads, color: 'success.main' },
                  { label: 'Lost Deals', value: stats.lostLeads, color: 'error.main' },
                  { label: 'Pending', value: stats.pendingApprovals, color: 'warning.main' },
                  { label: 'Converted', value: stats.convertedQuotations, color: 'info.main' }
                ].map(s => (
                  <Grid item xs={6} sm={3} key={s.label}>
                    <Box textAlign="center">
                      <Typography variant="h3" color={s.color} fontWeight="bold"
                        sx={{ fontSize: { xs: '1.6rem', sm: '2.2rem' } }}>
                        {s.value}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontSize: { xs: '0.72rem', sm: '0.875rem' } }}>
                        {s.label}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
              <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid', borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" fontWeight="bold">Sales Progress</Typography>
                  <Typography variant="body2" color="text.secondary">{calculateConversionRate()}</Typography>
                </Box>
                <Box sx={{ width: '100%', height: 8, bgcolor: '#e0e0e0', borderRadius: 4, overflow: 'hidden' }}>
                  <Box sx={{
                    width: `${Math.min(100, (stats.wonLeads / (stats.totalLeads || 1)) * 100)}%`,
                    height: '100%',
                    background: 'linear-gradient(90deg, #4caf50, #8bc34a)',
                    transition: 'width 0.5s ease'
                  }} />
                </Box>
              </Box>
            </Paper>
          </Grid>

          {/* Tasks Widget — real data */}
          <Grid item xs={12} md={4}>
            <Paper sx={{
              p: { xs: 2, sm: 3 }, borderRadius: 3,
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
              border: '1px solid', borderColor: 'divider',
              height: '100%',
              background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <TaskIcon color="primary" />
                <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: { xs: '1rem', sm: '1.1rem' } }}>
                  {user?.role === 'admin' ? 'Team Tasks' : 'My Tasks'}
                </Typography>
              </Box>

              {/* Stats row */}
              <Grid container spacing={1} sx={{ mb: 2 }}>
                {[
                  { label: 'Total', value: tasksStats.total, color: 'primary' },
                  { label: 'Pending', value: tasksStats.pending, color: 'warning.main' },
                  { label: 'Overdue', value: tasksStats.overdue, color: 'error.main' }
                ].map(s => (
                  <Grid item xs={4} key={s.label}>
                    <Box textAlign="center" sx={{ p: 0.75 }}>
                      <Typography variant="h5" color={s.color} fontWeight="bold"
                        sx={{ fontSize: { xs: '1.3rem', sm: '1.5rem' } }}>
                        {tasksLoading ? <CircularProgress size={16} /> : s.value}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" fontWeight="bold"
                        sx={{ fontSize: '0.68rem' }}>
                        {s.label}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>

              {/* Today's tasks */}
              <Typography variant="caption" color="text.secondary"
                sx={{ fontWeight: 700, letterSpacing: 0.5, display: 'block', mb: 1 }}>
                📅 TODAY ({todayTasks.length})
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
                {tasksLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                    <CircularProgress size={20} />
                  </Box>
                ) : todayTasks.length === 0 ? (
                  <Typography variant="caption" color="text.secondary"
                    sx={{ display: 'block', textAlign: 'center', py: 1.5, fontStyle: 'italic' }}>
                    No tasks due today 🎉
                  </Typography>
                ) : (
                  todayTasks.slice(0, 3).map((task, i) => (
                    <Box key={task.id || i} sx={{
                      display: 'flex', alignItems: 'flex-start', gap: 1,
                      p: 1.25, borderRadius: 1.5, bgcolor: 'white',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                      border: '1px solid',
                      borderColor: task.priority === 'urgent' ? 'error.light' : 'divider'
                    }}>
                      <Box sx={{
                        width: 10, height: 10, borderRadius: '50%', mt: 0.4, flexShrink: 0,
                        bgcolor: task.priority === 'urgent' ? 'error.main' :
                                 task.priority === 'high' ? 'warning.main' :
                                 task.priority === 'medium' ? 'info.main' : 'success.main'
                      }} />
                      <Typography variant="body2" sx={{ fontSize: '0.78rem', lineHeight: 1.35, flex: 1 }}>
                        {task.title}
                      </Typography>
                    </Box>
                  ))
                )}
                {todayTasks.length > 3 && (
                  <Typography variant="caption" color="primary"
                    sx={{ display: 'block', textAlign: 'center' }}>
                    +{todayTasks.length - 3} more tasks today
                  </Typography>
                )}
              </Box>

              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button variant="contained" startIcon={<AddIcon />}
                  onClick={() => navigate('/tasks')} size="small"
                  sx={{ flex: 1, borderRadius: 2, fontWeight: 'bold', fontSize: '0.78rem' }}>
                  Add Task
                </Button>
                <Button variant="outlined" onClick={() => navigate('/tasks')} size="small"
                  sx={{ borderRadius: 2, fontWeight: 'bold', fontSize: '0.78rem' }}>
                  View All
                </Button>
              </Box>
            </Paper>
          </Grid>
        </Grid>

        {/* Today's Calls Widget */}
        <Paper sx={{
          p: { xs: 2, sm: 3 }, borderRadius: 3, mb: 4,
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          border: '1px solid', borderColor: 'divider'
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PhoneIcon color="primary" />
              <Typography variant="h6" fontWeight="bold">
                {user?.role === 'admin' ? "Team's Calls Today" : "My Calls Today"}
              </Typography>
            </Box>
            <Button size="small" variant="outlined" onClick={() => navigate('/call-logs')}
              sx={{ borderRadius: 2, fontWeight: 'bold', fontSize: '0.75rem' }}>
              View All
            </Button>
          </Box>

          {/* Mini stats */}
          <Grid container spacing={1.5} sx={{ mb: 2 }}>
            {[
              { label: 'Today', value: callStats.today || 0, color: 'primary.main' },
              { label: 'Total', value: callStats.total || 0, color: 'text.secondary' },
              { label: 'Interested', value: callStats.interested || 0, color: 'success.main' },
              { label: 'Callbacks', value: callStats.callback || 0, color: 'warning.main' },
            ].map(s => (
              <Grid item xs={3} key={s.label}>
                <Box textAlign="center" sx={{
                  p: 1, borderRadius: 2, bgcolor: 'grey.50', border: '1px solid', borderColor: 'divider'
                }}>
                  <Typography fontWeight="bold" color={s.color}
                    sx={{ fontSize: { xs: '1.2rem', sm: '1.5rem' } }}>
                    {s.value}
                  </Typography>
                  <Typography variant="caption" color="text.secondary"
                    sx={{ fontSize: { xs: '0.62rem', sm: '0.72rem' } }}>
                    {s.label}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>

          {/* Today's call list */}
          {todayCalls.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="body2" color="text.secondary" fontStyle="italic">
                No calls logged today yet
              </Typography>
              <Button size="small" variant="contained" startIcon={<PhoneIcon />}
                onClick={() => navigate('/call-logs')} sx={{ mt: 1, borderRadius: 2 }}>
                Log a Call
              </Button>
            </Box>
          ) : (
            <Grid container spacing={1}>
              {todayCalls.map(call => {
                const outcomeColor = {
                  interested: 'success', callback: 'info', not_interested: 'default',
                  no_answer: 'warning', wrong_number: 'default', rejected: 'error'
                }[call.outcome] || 'default';
                const outcomeLabel = {
                  interested: 'Interested', callback: 'Callback', not_interested: 'Not Interested',
                  no_answer: 'No Answer', wrong_number: 'Wrong Number', rejected: 'Rejected'
                }[call.outcome] || call.outcome;
                return (
                  <Grid item xs={12} sm={6} md={4} key={call.id}>
                    <Box sx={{
                      p: 1.5, borderRadius: 2, border: '1px solid', borderColor: 'divider',
                      bgcolor: 'background.paper', display: 'flex', gap: 1.5, alignItems: 'flex-start'
                    }}>
                      <PhoneIcon sx={{ fontSize: 18, color: 'primary.main', mt: 0.2, flexShrink: 0 }} />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="body2" fontWeight={600} noWrap sx={{ fontSize: '0.8rem' }}>
                            {call.customer_name}
                          </Typography>
                          <Chip label={outcomeLabel} color={outcomeColor} size="small"
                            sx={{ fontSize: '0.6rem', height: 18 }} />
                        </Box>
                        {call.notes && (
                          <Typography variant="caption" color="text.secondary"
                            sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {call.notes}
                          </Typography>
                        )}
                        <Typography variant="caption" color="text.disabled">
                          {new Date(call.called_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                          {user?.role !== 'sales' && call.created_by_name ? ` · ${call.created_by_name}` : ''}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </Paper>

        {/* System Modules — 4 per row on desktop */}
        <Typography variant="h5" gutterBottom sx={{ mb: 2, fontWeight: 'bold' }}>
          System Modules
        </Typography>
        <Grid container spacing={{ xs: 1.5, sm: 2 }} sx={{ mb: 4 }}>
          {systemModules.map(mod => (
            <Grid item xs={6} sm={4} md={3} key={mod.title}>
              <FeatureCard {...mod} />
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* User Menu */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}
        PaperProps={{ sx: { borderRadius: 2, mt: 1 } }}>
        <MenuItem onClick={() => { setMyProfileOpen(true); setAnchorEl(null); }}>
          My Profile
        </MenuItem>
        <MenuItem onClick={() => { setChangePasswordOpen(true); setAnchorEl(null); }}>
          Change Password
        </MenuItem>
        <MenuItem onClick={() => { setAnchorEl(null); onLogout(); }}>
          Logout
        </MenuItem>
      </Menu>

      <ChangePasswordModal
        open={changePasswordOpen}
        onClose={() => setChangePasswordOpen(false)}
        user={user}
      />
      <MyProfileModal
        open={myProfileOpen}
        onClose={() => setMyProfileOpen(false)}
      />
    </>
  );
}
